import { Link, Outlet, useLocation, type LinkProps } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { useCurrentUser } from '@/hook/useCurrentUser';
import { AppErrorBoundary } from './AppErrorBoundary';
import { AvatarMenu } from './AvatarMenu';
import { BillingModals } from './BillingModals';
import { Footer } from './Footer';
import { PlanChip } from './PlanChip';
import { Wordmark } from './Wordmark';

// Library owns pack detail and the manager; Friends owns public profiles.
const NAV = [
  { label: 'Home', to: '/', isActive: (path: string) => path === '/' },
  { label: 'Library', to: '/library', isActive: (path: string) => path.startsWith('/library') },
  { label: 'Billboard', to: '/billboard', isActive: (path: string) => path.startsWith('/billboard') },
  { label: 'History', to: '/history', isActive: (path: string) => path.startsWith('/history') },
  {
    label: 'Friends',
    to: '/friends',
    isActive: (path: string) => path.startsWith('/friends') || path.startsWith('/u/'),
  },
  { label: 'Shop', to: '/shop', isActive: (path: string) => path.startsWith('/shop') },
] as const satisfies ReadonlyArray<{ label: string; to: LinkProps['to']; isActive: (path: string) => boolean }>;

/** Signed-in shell: text nav, plan chip, avatar menu, page container, footer,
 * and the plan and checkout modals any page can open. */
export function Layout() {
  const pathname = useLocation({ select: (location) => location.pathname });
  const { user, isAdmin } = useCurrentUser();

  return (
    <BillingModals>
    <div className="flex min-h-screen flex-col">
      <header className="flex flex-wrap items-center justify-between gap-6 border-b border-line px-10 py-4">
        <div className="flex flex-wrap items-center gap-9">
          <Wordmark />
          <nav className="flex gap-6">
            {NAV.map((item) => {
              const active = item.isActive(pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'whitespace-nowrap pb-1 text-sm font-medium',
                    active ? 'text-ink shadow-[inset_0_-2px_0_var(--accFill)]' : 'text-mute hover:text-ink'
                  )}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
        {user && (
          <div className="flex items-center gap-[14px]">
            <PlanChip user={user} />
            <AvatarMenu user={user} isAdmin={isAdmin} />
          </div>
        )}
      </header>
      <main className="mx-auto w-full max-w-page flex-1 px-10 pb-24">
        {/* Page-scoped boundary: a crashing page (bad :trackId, gone doc) must
            not take the nav down with it. */}
        <AppErrorBoundary>
          <Outlet />
        </AppErrorBoundary>
      </main>
      <Footer />
    </div>
    </BillingModals>
  );
}
